import type { ToolPart } from './ApprovalActions';
import type {
  DetailRow,
  ReadableBlock,
  ToolPresentation,
} from './toolPresenters';

// Readable views of the chat's code sandbox tools (crm-chat-api
// tools/sandbox.ts, #24995): a terminal-style view for sandbox_exec, and path
// + size for the file tools. A file's content is never shown, not even under
// "Technical details": it can hold CRM data (load_artifact copies a user's
// file in), and the admin chat viewer must not show users' files (D13). The
// chat API already strips it from the admin conversation response
// (redactSandboxFileContent); this keeps it off screen everywhere else.

export const SANDBOX_TOOL_NAMES = [
  'sandbox_exec',
  'sandbox_write_file',
  'sandbox_read_file',
  'load_artifact',
] as const;

export const SANDBOX_EXEC_TOOL_NAME = 'sandbox_exec';

export const isSandboxToolName = (toolName: string): boolean =>
  SANDBOX_TOOL_NAMES.some(name => name === toolName);

export type SandboxNetwork = 'online' | 'offline';

export type SandboxPresenterLabels = {
  command: string;
  timeLimit: string;
  seconds: (seconds: number) => string;
  online: string;
  offline: string;
  onlineHint: string;
  offlineHint: string;
  stdout: string;
  stderr: string;
  noOutput: string;
  truncated: string;
  timedOut: string;
  path: string;
  size: string;
  fileSize: (bytes: number) => string;
  busy: string;
  fromSandbox: string;
  file: string;
};

export type SandboxExecResult = {
  exitCode: number;
  stdout: string;
  stderr: string;
  truncated: boolean;
  timedOut: boolean;
  network: SandboxNetwork | null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value != null && !Array.isArray(value);

const str = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

const parseOutput = (output: unknown): Record<string, unknown> | null => {
  if (isRecord(output)) {
    return output;
  }
  if (typeof output !== 'string') {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(output);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null; // plain text from the tool: the Result shows it raw
  }
};

const networkOf = (value: unknown): SandboxNetwork | null =>
  value === 'online' || value === 'offline' ? value : null;

export const parseSandboxExecResult = (
  output: unknown,
): SandboxExecResult | null => {
  const record = parseOutput(output);
  if (record == null || typeof record.exitCode !== 'number') {
    return null;
  }
  return {
    exitCode: record.exitCode,
    stdout: typeof record.stdout === 'string' ? record.stdout : '',
    stderr: typeof record.stderr === 'string' ? record.stderr : '',
    truncated: record.truncated === true,
    timedOut: record.timedOut === true,
    network: networkOf(record.network),
  };
};

// The tools answer `{ error }` (e.g. "Sandbox busy: …" when the per-user or
// global limit is hit) instead of failing the call, so the model can react.
export type SandboxFailure =
  | { kind: 'busy'; message: string }
  | { kind: 'error'; message: string };

const BUSY = /^sandbox busy\b/i;

const failureOf = (
  record: Record<string, unknown> | null,
): SandboxFailure | null => {
  const message = str(record?.error);
  if (message == null) {
    return null;
  }
  return BUSY.test(message)
    ? { kind: 'busy', message }
    : { kind: 'error', message };
};

export const sandboxFailureOf = (part: ToolPart): SandboxFailure | null =>
  isSandboxToolName(part.toolName) && part.state === 'output-available'
    ? failureOf(parseOutput(part.output))
    : null;

export const sandboxFailureText = (
  failure: SandboxFailure,
  labels: SandboxPresenterLabels,
): string => (failure.kind === 'busy' ? labels.busy : failure.message);

// Raw input/output under "Technical details" (and in the admin chat viewer)
// minus the file content of sandbox_write_file and sandbox_read_file, and
// the page images of preview_document (base64 the model looked at).
export const withoutSandboxFileContent = (
  toolName: string,
  value: unknown,
): unknown => {
  if (toolName === 'preview_document') {
    const record = parseOutput(value);
    if (record == null || !Array.isArray(record.images)) {
      return value;
    }
    const { images, ...rest } = record;
    return { ...rest, images: images.length };
  }
  if (toolName !== 'sandbox_write_file' && toolName !== 'sandbox_read_file') {
    return value;
  }
  // parseOutput also covers an output sent as a JSON string.
  const record = parseOutput(value);
  if (record == null || !('content' in record)) {
    return value;
  }
  const { content: _content, ...rest } = record;
  return rest;
};

// The network chip only: a failed command reads from its error output.
const badgeBlock = (
  network: SandboxNetwork | null,
  labels: SandboxPresenterLabels,
): Array<ReadableBlock> => {
  const items: Extract<ReadableBlock, { kind: 'badges' }>['items'] = [];
  if (network != null) {
    items.push({
      label: network === 'online' ? labels.online : labels.offline,
      tone: 'neutral',
      icon: network,
      hint: network === 'online' ? labels.onlineHint : labels.offlineHint,
    });
  }
  return items.length > 0 ? [{ kind: 'badges', items }] : [];
};

const failureBlocks = (
  record: Record<string, unknown> | null,
  labels: SandboxPresenterLabels,
): Array<ReadableBlock> | null => {
  const failure = failureOf(record);
  return failure != null
    ? [{ kind: 'note', text: sandboxFailureText(failure, labels) }]
    : null;
};

const execOutputBlocks = (
  result: SandboxExecResult,
  labels: SandboxPresenterLabels,
): Array<ReadableBlock> => {
  const blocks: Array<ReadableBlock> = badgeBlock(result.network, labels);
  if (result.timedOut) {
    blocks.push({ kind: 'note', text: labels.timedOut });
  }
  if (result.stdout.length > 0) {
    blocks.push({
      kind: 'collapsible',
      title: labels.stdout,
      text: result.stdout,
    });
  }
  if (result.stderr.length > 0) {
    blocks.push({
      kind: 'collapsible',
      title: labels.stderr,
      text: result.stderr,
    });
  }
  if (result.stdout.length === 0 && result.stderr.length === 0) {
    blocks.push({ kind: 'note', text: labels.noOutput });
  }
  if (result.truncated) {
    blocks.push({ kind: 'note', text: labels.truncated });
  }
  return blocks;
};

const presentExec = (
  input: Record<string, unknown>,
  output: unknown,
  labels: SandboxPresenterLabels,
): ToolPresentation => {
  const inputBlocks: Array<ReadableBlock> = [];
  const command = str(input.command);
  if (command != null) {
    inputBlocks.push({ kind: 'command', text: command });
  }
  if (typeof input.timeoutSec === 'number') {
    inputBlocks.push({
      kind: 'rows',
      rows: [
        { label: labels.timeLimit, value: labels.seconds(input.timeoutSec) },
      ],
    });
  }
  if (output === undefined) {
    return { input: inputBlocks, output: [], complete: true };
  }
  const record = parseOutput(output);
  const result = parseSandboxExecResult(output);
  return {
    input: inputBlocks,
    output:
      failureBlocks(record, labels) ??
      (result != null ? execOutputBlocks(result, labels) : 'raw'),
    complete: true,
  };
};

const sizeOf = (record: Record<string, unknown>): number | null =>
  typeof record.sizeBytes === 'number' ? record.sizeBytes : null;

// sandbox_write_file, sandbox_read_file and load_artifact: the file's name,
// path and size. The content in the input (write) or the output (read) is
// deliberately left out.
const presentFileTool = (
  input: Record<string, unknown>,
  output: unknown,
  labels: SandboxPresenterLabels,
): ToolPresentation => {
  const inputPath = str(input.path);
  const inputBlocks: Array<ReadableBlock> =
    inputPath != null
      ? [{ kind: 'rows', rows: [{ label: labels.path, value: inputPath }] }]
      : [];
  if (output === undefined) {
    return { input: inputBlocks, output: [], complete: true };
  }
  const record = parseOutput(output);
  if (record == null) {
    return { input: inputBlocks, output: 'raw', complete: true };
  }
  const failure = failureBlocks(record, labels);
  if (failure != null) {
    return { input: inputBlocks, output: failure, complete: true };
  }
  const rows: Array<DetailRow> = [];
  const name = str(record.name);
  if (name != null) {
    rows.push({ label: labels.file, value: name });
  }
  const path = str(record.path) ?? inputPath;
  if (path != null) {
    rows.push({ label: labels.path, value: path });
  }
  const size = sizeOf(record);
  if (size != null) {
    rows.push({ label: labels.size, value: labels.fileSize(size) });
  }
  return {
    input: inputBlocks,
    output: [
      ...(rows.length > 0 ? [{ kind: 'rows' as const, rows }] : []),
      ...badgeBlock(networkOf(record.network), labels),
    ],
    complete: true,
  };
};

export const presentSandboxTool = (
  toolName: string,
  input: Record<string, unknown>,
  output: unknown,
  labels: SandboxPresenterLabels,
): ToolPresentation | null => {
  switch (toolName) {
    case 'sandbox_exec':
      return presentExec(input, output, labels);
    case 'sandbox_write_file':
    case 'sandbox_read_file':
    case 'load_artifact':
      return presentFileTool(input, output, labels);
    default:
      return null;
  }
};
